import type { CSSProperties, KeyboardEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { renderMarkdown } from '../lib/markdown';
import { useBindings } from '../state/BindingsContext';
import type { SelectionField } from '../state/useSelectionContext';

/**
 * Click-to-edit presentation copy.
 *
 * A click enters edit mode, blur, Enter, or the approval control saves;
 * Shift+Enter inserts a line break and Escape cancels.
 * A click that ends a text selection does NOT enter edit mode — that gesture
 * belongs to the selection-context chip.
 */
interface EditableTextProps {
  value: string | undefined;
  field: SelectionField;
  placeholder: string;
  readOnly: boolean;
  onSave: (next: string) => void;
  /** Visual role; drives typography, not behaviour. */
  variant?: 'headline' | 'body' | 'rationale' | 'lede';
  /** Body copy renders through the minimal markdown renderer when idle. */
  markdown?: boolean;
  /**
   * V5 page density: visually clamps idle copy to N lines (CSS line-clamp) so
   * overflow is truncated, never shrunk. Pair with `wordBudget` to also show a
   * quiet over-budget indicator - but only while editing is possible
   * (readOnly === false); it never appears in presentation, share or print.
   */
  clampLines?: number;
  wordBudget?: number;
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

export function EditApprovalButton({ onApprove }: { onApprove: () => void }): ReactElement {
  return (
    <button
      type="button"
      className="edit-approval"
      aria-label="Approve edit"
      title="Approve edit (Enter)"
      onPointerDown={(event) => event.preventDefault()}
      onClick={onApprove}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m5 12.5 4.25 4.25L19 7" />
      </svg>
    </button>
  );
}

export function EditableText({
  value,
  field,
  placeholder,
  readOnly,
  onSave,
  variant = 'body',
  markdown = false,
  clampLines,
  wordBudget,
}: EditableTextProps): ReactElement | null {
  const resolve = useBindings();
  const text = value ?? '';
  const resolvedText = resolve(text);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(resolvedText);
  const [idleHeight, setIdleHeight] = useState<number | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const idleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) setDraft(resolvedText);
  }, [resolvedText, editing]);

  useEffect(() => {
    if (!editing) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  if (readOnly && !text) return null;

  if (editing) {
    const commit = () => {
      setEditing(false);
      const next = draft.trim();
      if (next !== resolvedText.trim()) onSave(next);
    };
    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDraft(resolvedText);
        setEditing(false);
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        commit();
      }
    };
    return (
      <div
        className={`editable editable-${variant} editable-editor`}
        data-field={field}
        style={idleHeight === null ? undefined : { height: idleHeight }}
      >
        <textarea
          ref={ref}
          className="editable-input"
          value={draft}
          aria-label={`Edit ${field}`}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
          rows={1}
        />
        <EditApprovalButton onApprove={commit} />
      </div>
    );
  }

  const onClick = () => {
    if (readOnly) return;
    // A click that completed a drag-selection arms the chat context instead.
    if (window.getSelection()?.isCollapsed === false) return;
    setIdleHeight(idleRef.current?.offsetHeight ?? null);
    setDraft(resolvedText);
    setEditing(true);
  };

  const count = wordBudget !== undefined ? wordCount(resolvedText) : null;
  const overBudget = count !== null && wordBudget !== undefined && count > wordBudget;
  const clampStyle: CSSProperties | undefined =
    clampLines !== undefined
      ? {
          display: '-webkit-box',
          WebkitLineClamp: clampLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : undefined;

  return (
    <>
      <div
        ref={idleRef}
        className={`editable editable-${variant}${text ? '' : ' editable-empty'}${
          readOnly ? '' : ' editable-live'
        }`}
        data-field={field}
        style={clampStyle}
        onClick={onClick}
        {...(readOnly ? null : { role: 'button', tabIndex: 0, title: 'Click to edit' })}
        onKeyDown={(event) => {
          if (readOnly) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            setEditing(true);
          }
        }}
      >
        {text ? (
          markdown ? (
            <div className="md">{renderMarkdown(resolvedText)}</div>
          ) : (
            resolvedText
          )
        ) : (
          <span className="editable-placeholder">{placeholder}</span>
        )}
      </div>
      {!readOnly && overBudget ? (
        <span className="word-budget-over" title={`${count} words - the page targets about ${wordBudget}`}>
          {count}/{wordBudget} words
        </span>
      ) : null}
    </>
  );
}

/** Bullet copy, edited as one line per bullet. */
export function EditableBullets({
  value,
  readOnly,
  onSave,
  placeholder = 'Add points, one per line',
}: {
  value: string[] | undefined;
  readOnly: boolean;
  onSave: (next: string[]) => void;
  placeholder?: string;
}): ReactElement | null {
  const resolve = useBindings();
  const items = value ?? [];
  const resolvedDraft = items.map(resolve).join('\n');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(resolvedDraft);
  const [idleHeight, setIdleHeight] = useState<number | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const idleRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!editing) setDraft(resolvedDraft);
  }, [resolvedDraft, editing]);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  if (items.length === 0 && readOnly) return null;

  if (editing) {
    const commit = () => {
      setEditing(false);
      const next = draft
        .split('\n')
        .map((line) => line.replace(/^\s*[-*]\s*/, '').trim())
        .filter(Boolean);
      if (next.join('\n') !== resolvedDraft) onSave(next);
    };
    return (
      <div
        className="editable editable-bullets editable-editor editable-bullets-editor"
        data-field="bullets"
        style={idleHeight === null ? undefined : { height: idleHeight }}
      >
        <textarea
          ref={ref}
          className="editable-input"
          value={draft}
          aria-label="Edit bullets"
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(resolvedDraft);
              setEditing(false);
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              commit();
            }
          }}
          rows={Math.max(items.length, 2)}
        />
        <EditApprovalButton onApprove={commit} />
      </div>
    );
  }

  return (
    <ul
      ref={idleRef}
      className={`editable editable-bullets${readOnly ? '' : ' editable-live'}`}
      data-field="bullets"
      {...(readOnly ? null : { title: 'Click to edit' })}
      onClick={() => {
        if (readOnly) return;
        if (window.getSelection()?.isCollapsed === false) return;
        setIdleHeight(idleRef.current?.offsetHeight ?? null);
        setDraft(resolvedDraft);
        setEditing(true);
      }}
    >
      {items.length > 0 ? (
        items.map((item, index) => <li key={index}>{resolve(item)}</li>)
      ) : (
        <li className="editable-placeholder">{placeholder}</li>
      )}
    </ul>
  );
}

/**
 * A bound plan number, click-to-edit.
 *
 * Editing posts a plan mutation — the same pipeline the agent uses — and the
 * refreshed proposal comes back from the server. The client never recomputes
 * anything that depends on this value.
 */
export function EditableMoney({
  value,
  display,
  readOnly,
  onSave,
  label,
}: {
  value: number | null;
  display: string | null;
  readOnly: boolean;
  onSave: (next: number) => void;
  label: string;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value === null ? '' : String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  if (editing) {
    const commit = () => {
      setEditing(false);
      const next = Number(draft.replace(/[^0-9.]/g, ''));
      if (Number.isFinite(next) && next !== value) onSave(Math.round(next));
    };
    return (
      <input
        ref={ref}
        className="bound bound-input"
        type="number"
        min={0}
        step={50}
        inputMode="numeric"
        aria-label={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(value === null ? '' : String(value));
            setEditing(false);
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
    );
  }

  if (readOnly) return <span className="bound">{display ?? '—'}</span>;

  return (
    <button
      type="button"
      className="bound bound-editable"
      title={`${label} — click to change`}
      onClick={() => setEditing(true)}
    >
      {display ?? 'Set'}
    </button>
  );
}
